import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { BLOG_LANGUAGES, type BlogLanguage } from '../../../../models/BlogArticle';

/**
 * /mcp-prawo is a Polish URL whose body the server renders from the bundled
 * Polish copy (prawmi-64fwp), so Polish is the language of the server HTML.
 */
export const PAGE_LANGUAGE: BlogLanguage = 'pl';

const NAMESPACE = 'start';

const subscribeNever = () => () => undefined;

/**
 * false on the server and during hydration, true afterwards (and on a
 * client-only mount), so the first client render matches the server HTML.
 */
const useHydrated = (): boolean =>
  useSyncExternalStore(
    subscribeNever,
    () => true,
    () => false,
  );

const toBlogLanguage = (language?: string): BlogLanguage | null => {
  const base = language?.split('-')[0]?.toLowerCase();
  return BLOG_LANGUAGES.find((candidate) => candidate === base) ?? null;
};

/**
 * Language of content the page reads outside i18next (the benchmark article
 * card comes from the blog store). The shared i18n instance's language is not
 * a safe source before hydration: it comes from `navigator` and localStorage
 * (Node 21+ reports `navigator.language` en-US on the server), while t() still
 * falls back to bundled Polish - the card would print an English title on the
 * server and a Polish one in a Polish browser, a hydration mismatch (the
 * prawmi-uucw bug of /ai-dla-ksiegowych). The server and the first client
 * render use the page language; after hydration the card follows the UI
 * language, once the page copy is loaded in it, i.e. together with the copy
 * around it.
 */
export const useContentLanguage = (): BlogLanguage => {
  const { i18n } = useTranslation(NAMESPACE);
  const hydrated = useHydrated();
  const uiLanguage = toBlogLanguage(i18n?.language);
  const copyLoaded = Boolean(uiLanguage && i18n?.hasResourceBundle?.(uiLanguage, NAMESPACE));
  return hydrated && uiLanguage && copyLoaded ? uiLanguage : PAGE_LANGUAGE;
};
